import { Card, CardContent, InputAdornment } from '@material-ui/core'
import { Autocomplete, Skeleton } from '@material-ui/lab'
import ShowToRoleAndStates from 'components/Common/Portal/ShowToRole'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { AddressForm } from 'components/OrganizationSettings/General/OrganizationProfile/Sections/PartyDetails/Forms/AddressForm'
import { useSellingParties } from 'components/ProjectPage/ProjectSupplier/useSuppliers'
import { PartyRelationsOperations } from 'controllers/Party/Relations/IPartyRelationsAPI'
import _ from 'lodash'
import { TextField } from 'mui-rff'
import React, { useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { SearchPartyAPI } from 'services/APIs/InternalAPI/SearchPartyAPI'
import { RootAppState } from 'store/configureStore'
import {
  CreateProjectArgs,
  SellingPartyDto,
} from '../../../../services/APIs/InternalAPI/internal-api.contracts'
import { SearchParty } from '../../SearchParty/SearchParty'
import './CreateProjectForm.scss'

type Props = {
  formId: string
  initialValues?: CreateProjectArgs
  onSubmit: (createProject: CreateProjectArgs) => Promise<CreateProjectArgs>
  isSellerRole: boolean
}

const Title = (props: { translationKey: string; children: string }) => (
  <LocalizedTypography
    translationKey={props.translationKey}
    variant="h6"
    color="textPrimary"
    component="p"
    className="create-project-form-title"
  >
    {props.children}
  </LocalizedTypography>
)

const ChildrenOrSkeleton = (props: {
  children: React.ReactElement
  isLoading: boolean
}) => {
  return props.isLoading ? (
    <Skeleton
      className="skeleton"
      variant="text"
      animation="wave"
      height="3em"
    />
  ) : (
    props.children
  )
}

export const CreateProjectForm = (props: Props) => {
  const [initialValues, setInitialValues] = useState<CreateProjectArgs>({
    ...props.initialValues,
    vatRatio: { value: 21, unit: '%' },
  })

  const { t } = useTranslation()

  const user = useAppSelector((state: RootAppState) => state.user, _.isEqual)
  const [partyDetailsLoading, setPartyDetailsLoading] = useState(false)

  const { sellingParties, loading: loadingSellingParties } = useSellingParties(
    !props.isSellerRole
  )

  const [selectedSupplier, setSelectedSupplier] =
    useState<SellingPartyDto>(null)

  useEffect(() => {
    if (
      sellingParties.length === 1 &&
      sellingParties[0]?.id &&
      sellingParties[0].id !== ''
    ) {
      setSelectedSupplier(sellingParties[0])
      setInitialValues((values) => {
        return {
          ...values,
          supplierId: sellingParties[0].id,
        }
      })
    }
  }, [sellingParties])

  const contactPersonInput = useRef(undefined)

  React.useEffect(() => {
    const fillBuyerPartyDataForQuotationRequest = async () => {
      if (!props.isSellerRole) {
        const partyAPI = new SearchPartyAPI(user.organizationContext.id)

        const partyDetails = await partyAPI.GetPartyDetails(
          user.organizationContext.id,
          (req) => setPartyDetailsLoading(req.isLoading)
        )

        setInitialValues((args) => ({
          ...args,
          buyerPartyDto: {
            ...partyDetails,
            contactPerson: {
              ...user.profile,
            },
          },
        }))
      }
    }

    fillBuyerPartyDataForQuotationRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isSellerRole])

  const validateProjectForm = (data: CreateProjectArgs) => {
    const requiredText = t('common:required')
    const error = {}

    if (!data.projectReference) error['projectReference'] = requiredText
    if (props.isSellerRole) {
      if (!data.orderNumber) error['orderNumber'] = requiredText

      if (!data.buyerPartyDto?.legalName)
        error['buyerPartyDto.legalName'] = requiredText
    } else {
      if (!data.supplierId) error['supplierId'] = requiredText
    }

    return error
  }

  const handleSubmit = (values: CreateProjectArgs) => {
    return props.onSubmit(values)
  }

  const formId = props.formId

  return (
    <div className="create-project-form">
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateProjectForm}
      >
        {(formProps) => {
          return (
            <form onSubmit={formProps.handleSubmit} id={formId}>
              <Card variant="outlined">
                <CardContent>
                  <Title translationKey="project:create-project--identify--title">
                    identify your project
                  </Title>
                  <div className="project-data-container">
                    <ShowToRoleAndStates
                      seller
                      isBuyerRole={!props.isSellerRole}
                    >
                      <TextField
                        name="orderNumber"
                        label={
                          <LocalizedTypography translationKey="common:order">
                            order number
                          </LocalizedTypography>
                        }
                        required
                        variant="filled"
                        inputProps={{
                          autoComplete: 'new-password',
                        }}
                        autoFocus
                      />
                    </ShowToRoleAndStates>
                    <TextField
                      name="projectReference"
                      label={
                        <LocalizedTypography translationKey="project:project-reference">
                          project reference
                        </LocalizedTypography>
                      }
                      required
                      variant="filled"
                      fullWidth
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                      autoFocus={!props.isSellerRole}
                    />
                    <ShowToRoleAndStates
                      seller
                      isBuyerRole={!props.isSellerRole}
                    >
                      <TextField
                        name="vatRatio.value"
                        label={
                          <LocalizedTypography translationKey="common:vat">
                            VAT (%)
                          </LocalizedTypography>
                        }
                        type="number"
                        variant="filled"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </ShowToRoleAndStates>
                    <ShowToRoleAndStates
                      buyer
                      isBuyerRole={!props.isSellerRole}
                    >
                      <Field name="supplierId">
                        {(fieldProps) => {
                          return (
                            <Autocomplete
                              options={sellingParties}
                              noOptionsText={t(
                                'common:no-options',
                                'no options'
                              )}
                              loadingText={t('common:loading')}
                              loading={
                                loadingSellingParties[
                                  PartyRelationsOperations.GetSellers
                                ]
                              }
                              fullWidth
                              getOptionSelected={(option, value) => {
                                return option.id === value.id
                              }}
                              autoSelect
                              getOptionLabel={(option) =>
                                option?.legalName ?? ''
                              }
                              disabled={sellingParties.length === 1}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  return
                                }
                                if (!value?.id) return
                                fieldProps.input.onChange(value?.id)
                                setSelectedSupplier(value)
                              }}
                              disableClearable
                              value={selectedSupplier}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="supplierId"
                                  label={
                                    <LocalizedTypography
                                      translationKey={'common:supplier'}
                                    >
                                      supplier
                                    </LocalizedTypography>
                                  }
                                  autoComplete="new-password"
                                  required
                                  variant="filled"
                                  fullWidth
                                />
                              )}
                            />
                          )
                        }}
                      </Field>
                    </ShowToRoleAndStates>
                  </div>
                </CardContent>
              </Card>
              <Card variant="outlined" className="create-project-section">
                <ShowToRoleAndStates seller isBuyerRole={!props.isSellerRole}>
                  <CardContent>
                    <div className="search-control-container">
                      <Title translationKey="project:buying-party">
                        buyer company
                      </Title>

                      <SearchParty
                        onPartyDetailsReceived={(buyerDto) => {
                          setInitialValues({
                            ...formProps.values,
                            buyerPartyDto: buyerDto,
                            vatRatio: buyerDto.vatRatio,
                          })

                          contactPersonInput.current?.focus()
                        }}
                        onPartyDetailsLoading={(isLoading) => {
                          if (isLoading) {
                            setInitialValues({
                              ...formProps.values,
                              buyerPartyDto: undefined,
                            })
                          }
                          setPartyDetailsLoading(isLoading)
                        }}
                        onPartyDetailsCleaned={() => {
                          setPartyDetailsLoading(false)
                          setInitialValues({
                            ...formProps.values,
                            vatRatio: { value: 21, unit: '%' },
                            buyerPartyDto: {
                              ...formProps.values.buyerPartyDto,
                              vatRatio: { value: 21, unit: '%' },
                              address: undefined,
                              coCCode: undefined,
                              partyId: undefined,
                              legalName: undefined,
                            },
                          })
                        }}
                        className="search-control"
                      />
                    </div>
                    <ChildrenOrSkeleton isLoading={partyDetailsLoading}>
                      <TextField
                        name="buyerPartyDto.legalName"
                        label={
                          <LocalizedTypography translationKey="common:company-name">
                            company name
                          </LocalizedTypography>
                        }
                        variant="filled"
                        InputProps={{
                          startAdornment: partyDetailsLoading && (
                            <InputAdornment position="start">
                              <LocalizedTypography translationKey="common:loading">
                                loading...
                              </LocalizedTypography>
                            </InputAdornment>
                          ),
                        }}
                        disabled={partyDetailsLoading}
                      />
                    </ChildrenOrSkeleton>
                  </CardContent>
                </ShowToRoleAndStates>

                <CardContent>
                  <Title translationKey="common:contact-person">
                    contact person
                  </Title>
                  <div className="contact-details">
                    <TextField
                      inputRef={contactPersonInput}
                      name="buyerPartyDto.contactPerson.firstName"
                      label={
                        <LocalizedTypography translationKey="common:first-name">
                          first name
                        </LocalizedTypography>
                      }
                      variant="filled"
                    />
                    <TextField
                      name="buyerPartyDto.contactPerson.lastName"
                      label={
                        <LocalizedTypography translationKey="common:last-name">
                          last name
                        </LocalizedTypography>
                      }
                      variant="filled"
                    />
                    <TextField
                      name="buyerPartyDto.contactPerson.email"
                      label={
                        <LocalizedTypography translationKey="common:email">
                          email
                        </LocalizedTypography>
                      }
                      variant="filled"
                      type="email"
                    />
                  </div>
                </CardContent>
                <CardContent>
                  <Title translationKey="project:buying-party-address">
                    buyer address
                  </Title>
                  <Field name="buyerPartyDto.address">
                    {(fieldProps) => {
                      return (
                        <AddressForm
                          required
                          address={fieldProps.input.value}
                          variant="filled"
                          onChangeAddress={(address) => {
                            fieldProps.input.onChange(address)
                          }}
                          loading={partyDetailsLoading}
                        />
                      )
                    }}
                  </Field>
                </CardContent>
              </Card>
            </form>
          )
        }}
      </Form>
    </div>
  )
}
